import { graphql } from "./generated/index.js"

// Change history (`record_changes`, the audit log). Every per-record read takes
// its filter as a `record_changes_bool_exp` built by `historyWhere`
// (services/history.ts), so which changes count as a record's history is
// decided in one place.

// A page of changes, newest first, with the total that match.
export const fetchRecordHistory = graphql(`
  query fetchRecordHistory($where: record_changes_bool_exp!, $limit: Int = 50, $offset: Int = 0) {
    changes: record_changes(
      where: $where
      order_by: { created_at: desc }
      limit: $limit
      offset: $offset
    ) {
      ...RecordChangeFields
    }
    aggregate: record_changes_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`)

// One matching change per acting person — the caller's where excludes changes
// with no person (`actor_person_id: { _is_null: false }`).
export const fetchHistoryUsers = graphql(`
  query fetchHistoryUsers($where: record_changes_bool_exp!) {
    users: record_changes(where: $where, distinct_on: actor_person_id) {
      actor_person {
        id
        name
      }
    }
  }
`)

// The number of matching changes per entity type, one where per type — for
// filter badges, in one request. Its aliases are the keys of HistoryCounts.
export const fetchHistoryCounts = graphql(`
  query fetchHistoryCounts(
    $record: record_changes_bool_exp!
    $contact: record_changes_bool_exp!
    $listing: record_changes_bool_exp!
    $source: record_changes_bool_exp!
    $tagging: record_changes_bool_exp!
    $attachment: record_changes_bool_exp!
    $link: record_changes_bool_exp!
  ) {
    record: record_changes_aggregate(where: $record) {
      aggregate {
        count
      }
    }
    contact: record_changes_aggregate(where: $contact) {
      aggregate {
        count
      }
    }
    listing: record_changes_aggregate(where: $listing) {
      aggregate {
        count
      }
    }
    source: record_changes_aggregate(where: $source) {
      aggregate {
        count
      }
    }
    tagging: record_changes_aggregate(where: $tagging) {
      aggregate {
        count
      }
    }
    attachment: record_changes_aggregate(where: $attachment) {
      aggregate {
        count
      }
    }
    link: record_changes_aggregate(where: $link) {
      aggregate {
        count
      }
    }
  }
`)

// Change rows by id, newest first.
export const fetchChangesByIds = graphql(`
  query fetchChangesByIds($ids: [Int!]!) {
    changes: record_changes(where: { id: { _in: $ids } }, order_by: { created_at: desc }) {
      ...RecordChangeFields
    }
  }
`)

// The changes a record is the primary subject of, oldest first, without the
// before/after snapshots — what a source revert plans from (the record's own
// field, contact and attachment writes; none of those has a secondary side).
export const fetchRecordOwnChanges = graphql(`
  query fetchRecordOwnChanges($recordId: Int!) {
    changes: record_changes(
      where: { record_id: { _eq: $recordId } }
      order_by: { created_at: asc }
    ) {
      id
      created_at
      operation
      provenance
      entity_type
      entity_id
      changes
    }
  }
`)

// The global audit log, optionally narrowed by `$where` (e.g. record type).
// An interface runs it itself (through `@auger/core/gql`) to keep it reactive.
export const fetchGlobalAuditLog = graphql(`
  query fetchGlobalAuditLog(
    $limit: Int = 50
    $offset: Int = 0
    $where: record_changes_bool_exp = {}
  ) {
    changes: record_changes(
      where: $where
      order_by: { created_at: desc }
      limit: $limit
      offset: $offset
    ) {
      ...RecordChangeFields
      record {
        ...RequiredRecordFields
      }
    }
    aggregate: record_changes_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`)
